import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";

export interface FileValidationError {
  code: string;
  message: string;
  actualValue?: unknown;
  requiredValue?: unknown;
}

@Component({
  selector: "app-drop-file-input",
  templateUrl: "./drop-file-input.component.html",
  styleUrls: ["./drop-file-input.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropFileInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DropFileInputComponent),
      multi: true,
    },
  ],
})
export class DropFileInputComponent implements ControlValueAccessor, Validator {
  @ViewChild("uploadFileInput") uploadFileInput: ElementRef;

  @Input() isValid: boolean = true;
  @Input() maxFileSize?: number;
  @Input() disabled: boolean = false;

  public acceptableFiles: string[] = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  public get acceptableFilesString(): string {
    return this.acceptableFiles.join(",");
  }

  private uploadedFile: File | null = null;
  public isDragOver: boolean = false;
  public validationErrors: FileValidationError[] = [];
  public isUploading: boolean = false;

  constructor() {}

  private onChange = (value: File | null) => {};
  private onTouched = () => {};

  writeValue(value: File | null): void {
    this.uploadedFile = value;
  }

  registerOnChange(fn: (value: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    if (!this.uploadedFile && this.isRequired(control)) {
      errors["required"] = { message: "File is required" };
    }

    if (this.validationErrors.length > 0) {
      this.validationErrors.forEach((error) => {
        errors[error.code] = {
          message: error.message,
          actualValue: error.actualValue,
          requiredValue: error.requiredValue,
        };
      });
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  private isRequired(control: AbstractControl): boolean {
    const validator = control.validator;
    if (validator) {
      const result = validator({} as AbstractControl);
      return result && result["required"];
    }
    return false;
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.handleFileUpload(file);
    }
    this.onTouched();
  }

  onDragOver(event: DragEvent) {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.handleFileUpload(file);
    }
    this.onTouched();
  }

  private isFileTypeAcceptable(file: File): boolean {
    return this.acceptableFiles.includes(file.type);
  }

  private isFileSizeInvalid(file: File): boolean {
    return this.maxFileSize && file.size > this.maxFileSize;
  }

  private validateFile(file: File): void {
    this.validationErrors = [];

    if (!this.isFileTypeAcceptable(file)) {
      this.validationErrors.push({
        code: "fileType",
        message: "File type not supported. Please upload PDF or DOCX files.",
        actualValue: file.type,
        requiredValue: this.acceptableFiles,
      });
    }

    if (this.isFileSizeInvalid(file)) {
      this.validationErrors.push({
        code: "maxFileSize",
        message: `File size exceeds ${this.formatFileSize(this.maxFileSize)}`,
        actualValue: file.size,
        requiredValue: this.maxFileSize,
      });
    }
  }

  private handleFileUpload(file: File) {
    this.validateFile(file);

    if (this.validationErrors.length > 0) {
      return;
    }

    this.uploadedFile = file;
    this.onChange(file);

    if (this.uploadFileInput) {
      this.uploadFileInput.nativeElement.value = "";
    }
  }

  removeFile() {
    this.uploadedFile = null;
    this.validationErrors = [];
    this.onChange(null);
    this.onTouched();

    if (this.uploadFileInput) {
      this.uploadFileInput.nativeElement.value = "";
    }
  }

  triggerFileInput() {
    if (this.disabled) return;
    if (this.uploadFileInput) {
      this.uploadFileInput.nativeElement.click();
    }
  }

  get fileIcon(): string {
    if (!this.uploadedFile) return "";
    if (this.uploadedFile.type === "application/pdf") {
      return "file-text";
    } else if (
      this.uploadedFile.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return "file-text";
    }
    return "file";
  }

  get fileSizeFormatted(): string {
    if (!this.uploadedFile) return "";
    return this.formatFileSize(this.uploadedFile.size);
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  get hasValidationErrors(): boolean {
    return this.validationErrors.length > 0;
  }
}
